import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { navItems } from './nav-items';
import { useRef, useEffect } from 'react';

// ===== 全局深海动态效果系统 =====
import StarField from '@/effects/galaxy/StarField';
import OceanBreathing from '@/effects/ocean/OceanBreathing';
import Jellyfish from '@/effects/animals/Jellyfish';

// ★ PWA 和性能优化组件
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { PWAUpdatePrompt } from '@/components/PWAInstallPrompt';
import { ProgressProvider } from '@/components/LoadingProgress';

// ★ UX 增强组件
import BackToTop from '@/components/ui/BackToTop';
import NetworkStatus from '@/components/ui/NetworkStatus';
import ReadingProgress from '@/components/ui/ReadingProgress';

/* ── 全局漂浮颗粒（跨页面粒子上升）── */
const GlobalParticles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let w, h, id;
    const dots = Array.from({ length: 50 }, () => ({
      x: Math.random(), y: Math.random(), r: Math.random() * 1.8 + 0.4,
      vy: -(Math.random() * 0.0003 + 0.0001),
      vx: (Math.random() - 0.5) * 0.00008,
      o: Math.random() * 0.4 + 0.15,
      p: Math.random() * 6.28,
    }));
    const resize = () => { w = c.width = c.offsetWidth; h = c.height = c.offsetHeight; };
    resize();
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      dots.forEach(d => {
        d.y += d.vy; d.x += d.vx + Math.sin(d.p) * 0.00006; d.p += 0.006;
        if (d.y < 0) { d.y = 1; d.x = Math.random(); }
        const a = d.o * (0.5 + 0.5 * Math.sin(d.p));
        ctx.beginPath(); ctx.arc(d.x * w, d.y * h, d.r, 0, 6.28);
        ctx.fillStyle = `rgba(145,225,255,${a})`; ctx.shadowColor = 'rgba(88,183,232,0.4)'; ctx.shadowBlur = 4; ctx.fill(); ctx.shadowBlur = 0;
      });
      id = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }} />;
};

/* ── 全局鱼群游动 ── */
const GlobalFishSchool = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    let w = window.innerWidth;
    const fishes = Array.from({ length: 6 }, (_, i) => ({
      x: Math.random() * w,
      y: 15 + Math.random() * 70, // percent
      size: 18 + Math.random() * 18,
      speed: 0.3 + Math.random() * 0.4,
      dir: i % 2 ? 1 : -1,
      phase: Math.random() * 6.28,
      color: ['rgba(135,220,255,0.5)', 'rgba(180,160,255,0.4)', 'rgba(160,240,220,0.45)', 'rgba(255,200,150,0.35)', 'rgba(200,220,255,0.45)', 'rgba(140,200,240,0.4'][i],
    }));
    let id;
    const draw = () => {
      c.width = window.innerWidth;
      c.height = window.innerHeight;
      const ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);
      fishes.forEach(f => {
        f.x += f.dir * f.speed;
        f.phase += 0.02;
        if (f.x > w + 60) f.x = -60;
        if (f.x < -60) f.x = w + 60;
        const bobY = Math.sin(f.phase) * 4;
        // 画鱼简身
        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.fillStyle = f.color;
        ctx.shadowColor = f.color;
        ctx.shadowBlur = 6;
        const px = f.x, py = (f.y / 100) * c.height + bobY;
        ctx.beginPath();
        ctx.ellipse(px, py, f.size, f.size * 0.4, 0, 0, 6.28);
        ctx.fill();
        // 尾鳍
        const tailX = f.dir > 0 ? px - f.size : px + f.size;
        ctx.beginPath();
        ctx.moveTo(tailX, py);
        ctx.lineTo(tailX - f.dir * f.size * 0.6, py - f.size * 0.35);
        ctx.lineTo(tailX - f.dir * f.size * 0.6, py + f.size * 0.35);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      });
      id = requestAnimationFrame(draw);
    };
    draw();
    const onResize = () => { w = window.innerWidth; };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', onResize); };
  }, []);
  return <canvas ref={ref} style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1, opacity: 0.9 }} />;
};

/* ── 全局水母群 ──
 * Apple 设计层次（黄金比例 φ=1.618 + 深度层级）：
 *   1 只主焦点（130px，黄金比例 62% 高度，14% 左偏移）— 视觉锚点
 *   3 只中等辅助（80-100px，中远景平衡）
 *   6 只小型点缀（35-55px，远景虚化呼吸）
 * 总计 10 只，覆盖画面 80% 深度层次
 */
const GlobalJellyfish = () => {
  const jellies = [
    // 主焦点 — 黄金比例位，最高透明度
    { opacity: 0.95, size: 130, delay: 0,  duration: 12, top: '58%', left: '14%' },
    // 中远景 — 三角平衡构图
    { opacity: 0.78, size: 95,  delay: 3,  duration: 11, top: '15%', left: '82%' },
    { opacity: 0.72, size: 88,  delay: 6,  duration: 13, top: '38%', left: '72%' },
    { opacity: 0.65, size: 80,  delay: 2,  duration: 11, top: '75%', left: '85%' },
    // 远景虚化 — 填充画面深度
    { opacity: 0.55, size: 55,  delay: 1,  duration: 14, top: '8%',  left: '32%'  },
    { opacity: 0.5,  size: 48,  delay: 5,  duration: 15, top: '82%', left: '48%'  },
    { opacity: 0.52, size: 52,  delay: 9,  duration: 13, top: '65%', left: '4%'   },
    { opacity: 0.45, size: 42,  delay: 7,  duration: 16, top: '25%', left: '50%'  },
    { opacity: 0.42, size: 38,  delay: 4,  duration: 17, top: '48%', left: '35%'  },
    { opacity: 0.4,  size: 35,  delay: 8,  duration: 18, top: '92%', left: '18%'  },
  ];
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', overflow: 'hidden' }}>
      {jellies.map((j, i) => (
        <div key={i} style={{ position: 'absolute', top: j.top, left: j.left }}>
          <Jellyfish opacity={j.opacity} size={j.size} delay={j.delay} duration={j.duration} />
        </div>
      ))}
    </div>
  );
};

const queryClient = new QueryClient();

/**
 * App — 西蒙的猫根组件
 * 
 * 全局深海氛围（所有页面共享）：
 *   - StarField: 3D 星空远景
 *   - OceanBreathing: 深海呼吸光晕
 *   - GlobalParticles: 漂浮颗粒上升
 *   - GlobalFishSchool: 鱼群横向游动
 *   - GlobalJellyfish: 漂浮水母（多点分布）
 * 
 * ★ PWA 功能：
 *   - ProgressProvider: 页面加载进度条
 *   - PWAInstallPrompt: PWA 安装引导
 *   - PWAUpdatePrompt: 应用更新提示
 */
const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ProgressProvider>
        <HashRouter>
          <Toaster />

          {/* ===== Layer 0: 全局深海氛围背景 ===== */}
          <StarField />
          <OceanBreathing />

          {/* ===== Layer 1: 全局海洋动态（所有页面）===== */}
          <GlobalParticles />
          <GlobalFishSchool />
          <GlobalJellyfish />

          {/* ===== Layer 10: 所有页面内容 ===== */}
          <div className="relative z-10" style={{ minHeight: '100vh' }}>
            <Routes>
              {navItems.map(({ to, page }) => (
                <Route key={to} path={to} element={page} />
              ))}
            </Routes>
          </div>

          {/* ===== PWA 功能层 ===== */}
          <PWAInstallPrompt />
          <PWAUpdatePrompt />

          {/* ===== UX 增强层 ===== */}
          <BackToTop />
          <NetworkStatus />
          <ReadingProgress />
        </HashRouter>
      </ProgressProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
